import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FiBriefcase,
  FiCheckCircle,
  FiEdit,
  FiPlus,
  FiSearch,
  FiUsers,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const emptyForm = {
  code: "",
  name: "",
  departmentId: "",
  isActive: true,
};

export default function DesignationManagement() {
  const { accessToken } = useAuth();

  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDesignation, setEditingDesignation] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError("");

    try {
      const [designationResponse, departmentResponse] =
        await Promise.all([
          apiRequest("/designations", { accessToken }),
          apiRequest("/departments?activeOnly=true", {
            accessToken,
          }),
        ]);

      setDesignations(designationResponse || []);
      setDepartments(departmentResponse || []);
    } catch (requestError) {
      setError(
        requestError.message || "Unable to load designations.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredDesignations = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return designations.filter((designation) => {
      const matchesSearch =
        !normalizedSearch ||
        designation.name.toLowerCase().includes(normalizedSearch) ||
        designation.code.toLowerCase().includes(normalizedSearch) ||
        designation.department?.name
          ?.toLowerCase()
          .includes(normalizedSearch);

      const matchesDepartment =
        departmentFilter === "All" ||
        designation.departmentId === departmentFilter;

      return matchesSearch && matchesDepartment;
    });
  }, [designations, search, departmentFilter]);

  const stats = useMemo(() => {
    return {
      total: designations.length,
      active: designations.filter(
        (designation) => designation.isActive,
      ).length,
      employees: designations.reduce(
        (sum, designation) =>
          sum + (designation._count?.assignments || 0),
        0,
      ),
      departments: new Set(
        designations
          .filter((designation) => designation.departmentId)
          .map((designation) => designation.departmentId),
      ).size,
    };
  }, [designations]);

  const openCreateModal = () => {
    setEditingDesignation(null);
    setModalOpen(true);
  };

  const openEditModal = (designation) => {
    setEditingDesignation(designation);
    setModalOpen(true);
  };

  const saveDesignation = async (form) => {
    setSaving(true);
    setError("");

    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        departmentId: form.departmentId || undefined,
        ...(editingDesignation
          ? { isActive: form.isActive }
          : {}),
      };

      await apiRequest(
        editingDesignation
          ? `/designations/${editingDesignation.id}`
          : "/designations",
        {
          method: editingDesignation ? "PATCH" : "POST",
          body: payload,
          accessToken,
        },
      );

      setModalOpen(false);
      setEditingDesignation(null);
      await loadData();
    } catch (requestError) {
      setError(
        requestError.message || "Unable to save designation.",
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (designation) => {
    const action = designation.isActive
      ? "deactivate"
      : "activate";

    if (
      !window.confirm(
        `Do you want to ${action} ${designation.name}?`,
      )
    ) {
      return;
    }

    setError("");

    try {
      if (designation.isActive) {
        await apiRequest(`/designations/${designation.id}`, {
          method: "DELETE",
          accessToken,
        });
      } else {
        await apiRequest(`/designations/${designation.id}`, {
          method: "PATCH",
          body: { isActive: true },
          accessToken,
        });
      }

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          `Unable to ${action} designation.`,
      );
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Designation Management
          </h1>

          <p className="mt-2 text-gray-500">
            Manage employee positions and job titles.
          </p>
        </div>

        <button
          onClick={openCreateModal}
          className="px-5 py-3 rounded-2xl bg-primary-800 text-white flex items-center gap-2 hover:bg-primary-900 transition"
        >
          <FiPlus />
          Add Designation
        </button>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard
          title="Total Designations"
          value={stats.total}
          icon={<FiBriefcase size={22} />}
          iconBg="bg-primary-50"
          iconColor="text-primary-700"
        />

        <StatCard
          title="Active Positions"
          value={stats.active}
          icon={<FiCheckCircle size={22} />}
          iconBg="bg-green-50"
          iconColor="text-green-600"
        />

        <StatCard
          title="Assigned Employees"
          value={stats.employees}
          icon={<FiUsers size={22} />}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />

        <StatCard
          title="Departments Used"
          value={stats.departments}
          icon={<FiBriefcase size={22} />}
          iconBg="bg-yellow-50"
          iconColor="text-yellow-600"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <FiSearch
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />

            <input
              type="text"
              placeholder="Search designation or department..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <select
            value={departmentFilter}
            onChange={(event) =>
              setDepartmentFilter(event.target.value)
            }
            className="px-4 py-3 rounded-2xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="All">All Departments</option>

            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">
            Designation List
          </h3>
        </div>

        {isLoading ? (
          <div className="min-h-72 flex items-center justify-center">
            <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-800 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                    Designation
                  </th>

                  <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                    Department
                  </th>

                  <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                    Employees
                  </th>

                  <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                    Status
                  </th>

                  <th className="px-6 py-4 text-center text-xs uppercase text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredDesignations.map((designation) => (
                  <tr
                    key={designation.id}
                    className="border-t border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">
                          {designation.name}
                        </p>

                        <span className="rounded-md bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-800">
                          {designation.code}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-5 text-gray-600">
                      {designation.department?.name || "Unassigned"}
                    </td>

                    <td className="px-6 py-5 font-semibold text-primary-700">
                      {designation._count?.assignments || 0}
                    </td>

                    <td className="px-6 py-5">
                      <StatusBadge isActive={designation.isActive} />
                    </td>

                    <td className="px-6 py-5">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => openEditModal(designation)}
                          className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center justify-center transition"
                          title="Edit designation"
                        >
                          <FiEdit />
                        </button>

                        <button
                          onClick={() => toggleStatus(designation)}
                          className={`px-3 h-10 rounded-xl text-xs font-semibold transition ${
                            designation.isActive
                              ? "bg-red-50 text-red-700 hover:bg-red-100"
                              : "bg-green-50 text-green-700 hover:bg-green-100"
                          }`}
                        >
                          {designation.isActive
                            ? "Deactivate"
                            : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {!filteredDesignations.length ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-16 text-center text-gray-500"
                    >
                      No designations found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DesignationModal
        open={modalOpen}
        initialData={editingDesignation}
        departments={departments}
        saving={saving}
        onClose={() => {
          if (!saving) {
            setModalOpen(false);
            setEditingDesignation(null);
          }
        }}
        onSave={saveDesignation}
      />
    </div>
  );
}

function DesignationModal({
  open,
  initialData,
  departments,
  saving,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;

    setForm(
      initialData
        ? {
            code: initialData.code || "",
            name: initialData.name || "",
            departmentId: initialData.departmentId || "",
            isActive: Boolean(initialData.isActive),
          }
        : emptyForm,
    );
  }, [open, initialData]);

  if (!open) return null;

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex justify-between items-center px-8 py-6 border-b border-gray-100">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {initialData
                ? "Edit Designation"
                : "Add Designation"}
            </h3>

            <p className="mt-1 text-sm text-gray-500">
              Assign job titles to an active department.
            </p>
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="h-10 w-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50"
          >
            <FiX size={20} />
          </button>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSave(form);
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-8">
            <div>
              <label className="block text-sm text-gray-600 mb-2">
                Designation Name *
              </label>

              <input
                required
                maxLength={150}
                value={form.name}
                onChange={(event) =>
                  updateField("name", event.target.value)
                }
                placeholder="Store Manager"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-2">
                Designation Code *
              </label>

              <input
                required
                maxLength={50}
                value={form.code}
                onChange={(event) =>
                  updateField(
                    "code",
                    event.target.value.toUpperCase(),
                  )
                }
                placeholder="STORE_MANAGER"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm text-gray-600 mb-2">
                Department
              </label>

              <select
                value={form.departmentId}
                onChange={(event) =>
                  updateField("departmentId", event.target.value)
                }
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                <option value="">No Department / General Role</option>

                {departments.map((department) => (
                  <option
                    key={department.id}
                    value={department.id}
                  >
                    {department.name}
                  </option>
                ))}
              </select>
            </div>

            {initialData ? (
              <div className="md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(event) =>
                      updateField("isActive", event.target.checked)
                    }
                    className="h-5 w-5 accent-primary-800"
                  />

                  <span className="text-sm font-medium text-gray-700">
                    Designation is active
                  </span>
                </label>
              </div>
            ) : null}
          </div>

          <div className="flex justify-end gap-3 px-8 py-5 border-t border-gray-100">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="px-5 py-3 rounded-2xl border border-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="px-5 py-3 rounded-2xl bg-primary-800 text-white disabled:opacity-50"
            >
              {saving
                ? "Saving..."
                : initialData
                  ? "Save Changes"
                  : "Save Designation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  iconBg,
  iconColor,
}) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-500">{title}</p>

          <h3 className="mt-3 text-3xl font-bold text-gray-900">
            {value}
          </h3>
        </div>

        <div
          className={`h-14 w-14 rounded-2xl flex items-center justify-center ${iconBg} ${iconColor}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ isActive }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${
        isActive
          ? "bg-green-50 text-green-700 border-green-100"
          : "bg-red-50 text-red-700 border-red-100"
      }`}
    >
      {isActive ? (
        <FiCheckCircle size={13} />
      ) : (
        <FiXCircle size={13} />
      )}

      {isActive ? "Active" : "Inactive"}
    </span>
  );
}